import React, { useState, useEffect } from 'react';
import { BookHeart, LogIn, Calculator } from 'lucide-react';
import PathSelectionModal from './modals/PathSelectionModal';
import AuthModal from '../auth/AuthModal';
import EmailCaptureModal from './modals/EmailCaptureModal';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

const frequentEvents = [
  'domingos de paella',
  'tuppers de comida casera',
  'partidas de cartas',
  'paseos por la tarde',
  'cafés compartidos',
  'sobremesas eternas',
  'abrazos de despedida',
  'consejos que valen oro',
  'historias por escuchar',
  'miradas que lo dicen todo',
  'veces para decir "te quiero"',
  'debates en la mesa',
];

const annualEvents = [
  'Navidades juntos',
  'veranos en el pueblo',
  'cumpleaños para celebrar',
  'cenas de Nochebuena',
  'días del Padre o de la Madre',
  'uvas de Año Nuevo',
  'reencuentros especiales',
  'domingos de paella',
  'tuppers de comida casera',
  'partidas de cartas',
  'paseos por la tarde',
  'cafés compartidos',
  'sobremesas eternas',
  'abrazos de despedida',
  'consejos que valen oro',
  'historias por escuchar',
  'miradas que lo dicen todo',
  'veces para decir "te quiero"',
  'debates en la mesa',
];

const generateDotMatrix = (totalLifeYears: number, livedYears: number) => {
  const totalDots = [];
  for (let i = 0; i < totalLifeYears; i++) {
    totalDots.push({
      key: i,
      status: i < livedYears ? 'lived' : 'remaining',
    });
  }
  return totalDots;
};

const STORAGE_KEY = 'calculator_email_captured';

const TimeTogetherCalculator = () => {
  const [parentGender, setParentGender] = useState<'male' | 'female' | null>(null);
  const [birthYear, setBirthYear] = useState<string>('');
  const [visitFrequency, setVisitFrequency] = useState<string>('');
  const [visitPeriod, setVisitPeriod] = useState<'Semana' | 'Mes' | 'Trimestre' | 'Año' | ''>('');
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [fadeClass, setFadeClass] = useState('opacity-100');
  const [modalState, setModalState] = useState<'closed' | 'selection' | 'recorder'>('closed');
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'forgot-password'>('register');
  const [scrolled, setScrolled] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const resultsRef = React.useRef<HTMLDivElement>(null);

  const lifeExpectancy = parentGender === 'female' ? 86.53 : parentGender === 'male' ? 81.1 : 86.53;
  const currentYear = new Date().getFullYear();
  const ageFloat = currentYear - (parseInt(birthYear) || currentYear);
  const age = Math.floor(ageFloat);
  const frequency = parseInt(visitFrequency) || 0;
  const totalLifeYears = Math.ceil(lifeExpectancy);

  const annualFrequency = (() => {
    switch (visitPeriod) {
      case 'Semana':
        return frequency * 52;
      case 'Mes':
        return frequency * 12;
      case 'Trimestre':
        return frequency * 4;
      case 'Año':
        return frequency * 1;
      default:
        return 0;
    }
  })();

  const currentEvents = visitPeriod === 'Año' ? annualEvents : frequentEvents;

  const yearsLeft = Math.max(0, lifeExpectancy - ageFloat);
  const totalVisits = Math.round(yearsLeft * annualFrequency);
  const matrixData = generateDotMatrix(totalLifeYears, age);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setCurrentEventIndex(0);
  }, [visitPeriod]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeClass('opacity-0');

      setTimeout(() => {
        setCurrentEventIndex((prev) => (prev + 1) % currentEvents.length);
        setFadeClass('opacity-100');
      }, 300);
    }, 2500);

    return () => clearInterval(interval);
  }, [currentEvents]);

  const handleStartClick = () => {
    setModalState('selection');
  };

  const handleSelectPresencial = () => {
    setModalState('closed');
    setAuthModalOpen(true);
    setAuthMode('register');
  };

  const handleSelectRemote = () => {
    setModalState('closed');
    setAuthModalOpen(true);
    setAuthMode('register');
  };

  const handleSaveRecording = () => {
    setModalState('closed');
    setAuthModalOpen(true);
    setAuthMode('register');
  };

  const handleLoginClick = () => {
    setAuthModalOpen(true);
    setAuthMode('login');
  };

  const isFormComplete = () => {
    return (
      parentGender !== null &&
      birthYear !== '' &&
      parseInt(birthYear) >= 1900 &&
      parseInt(birthYear) <= currentYear &&
      visitFrequency !== '' &&
      parseInt(visitFrequency) > 0 &&
      visitPeriod !== ''
    );
  };

  const hasEmailCaptured = () => {
    try {
      const captured = localStorage.getItem(STORAGE_KEY);
      return captured === 'true';
    } catch {
      return false;
    }
  };

  const scrollToResults = () => {
    setTimeout(() => {
      if (resultsRef.current) {
        const navHeight = 80;
        const elementPosition = resultsRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - navHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleCalculateClick = () => {
    if (!isFormComplete()) {
      toast.error('Por favor, completa todos los campos del formulario');
      return;
    }

    if (hasEmailCaptured()) {
      setShowResults(true);
      scrollToResults();
    } else {
      setEmailModalOpen(true);
    }
  };

  const handleEmailSubmit = async (email: string) => {
    setSavingLead(true);
    try {
      const { error } = await supabase.from('calculator_leads').insert([
        {
          email: email,
          parent_gender: parentGender,
          birth_year: parseInt(birthYear),
          age: age,
          visit_frequency: parseInt(visitFrequency),
          visit_period: visitPeriod,
          total_visits_calculated: totalVisits,
          life_expectancy: lifeExpectancy,
          user_agent: navigator.userAgent,
        },
      ]);

      if (error) throw error;

      localStorage.setItem(STORAGE_KEY, 'true');
      setEmailModalOpen(false);
      setShowResults(true);
      toast.success('¡Gracias! Aquí está tu resultado');

      scrollToResults();
    } catch (error: any) {
      console.error('Error saving lead:', error);
      throw new Error('Error al guardar. Por favor, intenta de nuevo.');
    } finally {
      setSavingLead(false);
    }
  };

  return (
    <div className="min-h-screen font-sans text-stone-800 bg-[#FAF9F6] antialiased selection:bg-rose-100 selection:text-rose-900">
      <nav className={`fixed w-full z-sticky transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <a href="/" className="flex items-center gap-3 cursor-pointer">
            <BookHeart size={36} className="text-rose-500" />
            <span className="text-2xl font-medium tracking-tight text-stone-800">Ethernal</span>
          </a>

          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={handleLoginClick}
              className="text-base text-stone-700 hover:text-stone-900 px-5 h-10 rounded-full hover:bg-stone-100 transition-colors flex items-center gap-2"
            >
              <LogIn size={18} />
              Iniciar sesión
            </button>

            <button
              onClick={handleStartClick}
              className="text-base bg-stone-900 text-white px-6 h-10 rounded-full hover:bg-stone-700 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 flex items-center"
            >
              Empezar Gratis
            </button>
          </div>

          <div className="md:hidden flex gap-3">
            <button
              onClick={handleLoginClick}
              className="text-stone-700 p-2"
            >
              <LogIn size={20} />
            </button>
            <button
              onClick={handleStartClick}
              className="bg-stone-900 text-white px-4 py-2 rounded-full text-sm"
            >
              Empezar
            </button>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-6xl font-medium leading-[1.05] text-stone-900 tracking-tight mb-6">
              ¿Cuántos <span className="text-rose-500 italic">momentos</span> quedan?
            </h1>
            <p className="text-xl md:text-2xl text-stone-600 leading-relaxed mb-4">
              La vida es finita. Haz que cada visita cuente.
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-stone-200">
            <div className="space-y-8">
              <div>
                <label className="block text-lg font-medium text-stone-900 mb-4">
                  1. ¿Los momentos con quién?
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col">
                    <button
                      onClick={() => setParentGender('female')}
                      className={`py-4 px-4 rounded-xl transition-all border ${
                        parentGender === 'female'
                          ? 'bg-rose-500 text-white shadow-md border-rose-500'
                          : 'bg-stone-100 text-stone-700 hover:bg-stone-200 border-stone-300'
                      }`}
                    >
                      Mujer
                    </button>
                    <p className="text-xs text-stone-500 mt-2 text-center">
                      Esperanza de vida: 86.53 años
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <button
                      onClick={() => setParentGender('male')}
                      className={`py-4 px-4 rounded-xl transition-all border ${
                        parentGender === 'male'
                          ? 'bg-rose-500 text-white shadow-md border-rose-500'
                          : 'bg-stone-100 text-stone-700 hover:bg-stone-200 border-stone-300'
                      }`}
                    >
                      Hombre
                    </button>
                    <p className="text-xs text-stone-500 mt-2 text-center">
                      Esperanza de vida: 81.1 años
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-1 items-end">
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-lg font-medium text-stone-900 mb-4">
                    2. ¿En qué año nació?
                  </label>
                  <input
                    type="number"
                    value={birthYear}
                    onChange={(e) => setBirthYear(e.target.value)}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value);
                      if (e.target.value !== '' && (isNaN(value) || value > currentYear || value < 1900)) {
                        setBirthYear('');
                      }
                    }}
                    placeholder="1954"
                    className="w-full py-3 px-4 text-xl font-semibold text-center rounded-xl border-2 border-stone-300 focus:border-rose-500 focus:outline-none transition-colors bg-white"
                    min="1900"
                    max={currentYear}
                  />
                </div>
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-lg font-semibold text-stone-900 mb-0 md:mb-4 invisible md:visible">
                    Edad
                  </label>
                  <div className="w-full py-3 px-4 text-xl font-semibold text-center rounded-xl border-2 border-stone-200 bg-stone-100 text-stone-800">
                    {age} años
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-lg font-medium text-stone-900 mb-4">
                  3. ¿Con qué frecuencia le ves?
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="number"
                    value={visitFrequency}
                    onChange={(e) => setVisitFrequency(e.target.value)}
                    placeholder="1"
                    className="w-20 py-3 px-4 text-xl font-semibold text-center rounded-xl border-2 border-stone-300 focus:border-rose-500 focus:outline-none transition-colors"
                    min="1"
                  />
                  <span className="text-lg text-stone-700">veces al</span>
                  <select
                    value={visitPeriod}
                    onChange={(e) => setVisitPeriod(e.target.value as 'Semana' | 'Mes' | 'Trimestre' | 'Año' | '')}
                    className="flex-1 min-w-[140px] py-3 px-4 text-lg rounded-xl border-2 border-stone-300 focus:border-rose-500 focus:outline-none transition-colors bg-white cursor-pointer"
                  >
                    <option value="" disabled>Seleccionar</option>
                    <option value="Semana">Semana</option>
                    <option value="Mes">Mes</option>
                    <option value="Trimestre">Trimestre</option>
                    <option value="Año">Año</option>
                  </select>
                </div>
              </div>

              {/* Calculate Button */}
              {!showResults && (
                <div className="pt-8 border-t-2 border-stone-200">
                  <div className="text-center">
                    <button
                      onClick={handleCalculateClick}
                      disabled={!isFormComplete()}
                      className={`bg-rose-500 text-white px-12 py-5 rounded-full text-xl font-semibold transition-all shadow-lg flex items-center gap-3 mx-auto ${
                        isFormComplete()
                          ? 'hover:bg-rose-600 hover:shadow-rose-500/30 transform hover:-translate-y-1 cursor-pointer'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <Calculator size={24} />
                      <span>Calcular Momentos</span>
                    </button>
                    {!isFormComplete() && (
                      <p className="text-sm text-stone-500 mt-4">
                        Completa todos los campos para calcular
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Results Section */}
              {showResults && (
                <div ref={resultsRef} className="pt-8 border-t-2 border-stone-200 scroll-mt-20">
                  <div className="text-center">
                    {totalVisits < 1 ? (
                    <div className="space-y-6 py-8">
                      <p className="text-3xl md:text-4xl text-stone-900 font-medium leading-relaxed">
                        Tienes mucha suerte de seguir teniendo <br className="hidden md:block" />
                        a tu ser querido <span className="text-rose-500">❤️</span>
                      </p>
                      <p className="text-2xl md:text-3xl text-rose-500 italic">
                        Aprovecha el tiempo con {parentGender === 'female' ? 'ella' : 'él'} 😊
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-8">
                        <p className="text-stone-600 text-xl mb-4">Te quedan aproximadamente</p>
                        <div className="text-7xl md:text-8xl font-semibold text-rose-500 leading-none mb-4">
                          {totalVisits.toLocaleString()}
                        </div>
                        <p className="text-stone-600 text-lg mb-4">
                          {totalVisits === 1 ? 'momento de' : 'momentos de'}
                        </p>
                        <p
                          className={`text-2xl md:text-3xl text-stone-800 italic transition-opacity duration-300 ${fadeClass} min-h-[40px] flex items-center justify-center mx-auto max-w-sm`}
                        >
                          {currentEvents[currentEventIndex]}
                        </p>
                      </div>

                      {/* Matriz de Puntos */}
                      <div className="p-4 md:p-6 bg-stone-50 rounded-xl border border-stone-200 shadow-inner">
                        <p className="text-center text-xs font-semibold uppercase text-stone-500 mb-4">
                          Vida transcurrida
                        </p>
                        <div className="grid grid-cols-10 gap-1 justify-center max-w-lg mx-auto">
                          {[...matrixData, ...Array(100 - matrixData.length).fill({ status: 'empty' })].map((dot, index) => (
                            <div
                              key={index}
                              className={`w-3 h-3 rounded-sm ${
                                dot.status === 'lived' ? 'bg-rose-500'
                                : dot.status === 'remaining' ? 'bg-stone-300'
                                : 'bg-transparent'
                              }`}
                              title={
                                dot.status === 'lived' ? `Año ${dot.key + 1} vivido`
                                : dot.status === 'remaining' ? `Año ${dot.key + 1} restante`
                                : 'Celda vacía'
                              }
                            />
                          ))}
                        </div>
                        <div className="flex justify-center gap-4 text-xs mt-4">
                          <span className="flex items-center gap-1 text-stone-700">
                            <div className="w-3 h-3 bg-rose-500 rounded-sm"></div> {age} Años Vividos
                          </span>
                          <span className="flex items-center gap-1 text-stone-500">
                            <div className="w-3 h-3 bg-stone-300 rounded-sm"></div> {Math.round(yearsLeft)} Años Restantes ({Math.ceil(lifeExpectancy)} total)
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>

          <div className="text-center mt-12 space-y-8">
            <p className="text-xl md:text-2xl text-stone-700 leading-relaxed max-w-2xl mx-auto">
              Cada uno de esos momentos es irrepetible. <br />
              <span className="font-medium text-stone-900">No esperes a mañana.</span>
            </p>

            <button
              onClick={handleStartClick}
              className="bg-rose-500 text-white px-10 py-5 rounded-full text-xl font-semibold hover:bg-rose-600 transition shadow-lg hover:shadow-rose-500/30 transform hover:-translate-y-1"
            >
              Empezar a preservar recuerdos
            </button>

            <div className="bg-stone-100 border border-stone-200 rounded-xl p-6 mt-8 text-left max-w-2xl mx-auto">
              <p className="text-sm text-stone-600 leading-relaxed">
                <span className="font-semibold text-stone-800">Sobre esta calculadora:</span> Los cálculos se basan en la esperanza de vida oficial en España (2024) según el Instituto Nacional de Estadística: 86.53 años para mujeres y 81.1 años para hombres. La fórmula es simple: (esperanza de vida - edad actual) × visitas al año = momentos restantes.
              </p>
            </div>
          </div>
        </div>
      </main>

      <PathSelectionModal
        isOpen={modalState !== 'closed'}
        onClose={() => setModalState('closed')}
        modalState={modalState}
        onSelectPresencial={handleSelectPresencial}
        onSelectRemote={handleSelectRemote}
        onBackToSelection={() => setModalState('selection')}
        onSaveRecording={handleSaveRecording}
        question="¿Cuál es tu primer recuerdo?"
      />

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        onSwitchMode={setAuthMode}
      />

      <EmailCaptureModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSubmit={handleEmailSubmit}
        loading={savingLead}
      />
    </div>
  );
};

export default TimeTogetherCalculator;
